import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Avatar, Box, CircularProgress, Paper, Stack, Typography, useTheme } from "@mui/material";
import { RootState } from "../../../../store/Index";
import apiService from "../../../../service/Api";
import { formatDateWithWeekday } from "../../../../utils/FormatDate";
import { tokens } from "../../../../theme";
import type { BackendStatus, BookingResponse } from "../../../bookings/types/list-bookings";

const STATUS_LABELS: Record<BackendStatus, string> = {
  pending: "Enquiry",
  confirmed: "Confirmed",
  completed: "Fulfilled",
  cancelled: "Cancelled",
  lost: "Lost",
  testing: "Testing",
};

const STATUS_DOT_COLOR: Record<BackendStatus, string> = {
  pending: "#F79E1B",
  confirmed: "#4BA663",
  completed: "#0000FF",
  cancelled: "#EB4335",
  lost: "#9e9e9e",
  testing: "#9e8383",
};

interface MyBookingRow {
  id: number;
  title: string;
  initials: string;
  imageUrl?: string | null;
  status: BackendStatus;
  dateOfService: string;
  venue: string;
  bookingType: string;
}

const getInitials = (value: string): string =>
  value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

const getRowInfo = (
  booking: BookingResponse,
): { title: string; initials: string; imageUrl?: string | null } => {
  const deceased = booking.deceased?.[0];
  if (deceased) {
    const fullName = `${deceased.first_name || ""} ${deceased.surname || ""}`.trim();
    return {
      title: fullName || "Deceased Name N/A",
      initials: getInitials(fullName || "N A"),
      imageUrl: deceased.person_image,
    };
  }
  if (booking.events?.[0]?.title) {
    const title = booking.events[0].title as string;
    return { title, initials: getInitials(title) };
  }
  if (booking.client && (booking.client.salutation || booking.client.company_name)) {
    const title = `${booking.client.salutation || ""} ${booking.client.company_name || ""}`.trim();
    return { title, initials: getInitials(title) };
  }
  const title = booking.title || "Booking N/A";
  return { title, initials: getInitials(title) };
};

const getVenue = (booking: BookingResponse): string => {
  const service = booking.services?.[0];
  const event = booking.events?.[0];
  const venueName = service?.venue_name || event?.venue_name;
  const venueAddress = service?.venue_address || event?.venue_address;
  if (venueName) {
    return venueAddress ? `${venueName}, ${venueAddress}` : venueName;
  }
  if (service?.church_name) return service.church_name;
  return "No venue set";
};

const MyAssignedBookings: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const userId = useSelector((state: RootState) => state.auth.user?.id);

  const [rows, setRows] = useState<MyBookingRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchMyBookings = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await apiService.get<{ results: BookingResponse[] } | BookingResponse[]>(
        "/api/bookings/list-bookings/",
        {
          sales_rep: userId,
          page_size: 8,
          ordering: "-id",
        },
      );
      const results = Array.isArray(response.data)
        ? response.data
        : response.data.results ?? [];

      const fetched = results.map((booking) => {
        const { title, initials, imageUrl } = getRowInfo(booking);
        return {
          id: booking.id,
          title,
          initials,
          imageUrl,
          status: booking.status,
          dateOfService: formatDateWithWeekday(
            booking.services?.[0]?.date_of_service ||
              booking.events?.[0]?.start_date_of_event ||
              "",
          ),
          venue: getVenue(booking),
          bookingType: booking.booking_type
            ? booking.booking_type.charAt(0).toUpperCase() +
              booking.booking_type.slice(1).toLowerCase()
            : "N/A",
        };
      });

      setRows(fetched);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchMyBookings();
  }, [fetchMyBookings]);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "24px",
        p: 2,
        height: "100%",
        backgroundColor: colors.primary[900],
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={800}
        color={colors.primary[700]}
        sx={{ display: "block", mb: 1.5 }}
      >
        MY ASSIGNED BOOKINGS
      </Typography>

      {loading ? (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={26} sx={{ color: colors.primary[400] }} />
        </Box>
      ) : rows.length === 0 ? (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" color={colors.primary[300]}>
            No bookings have been assigned to you yet.
          </Typography>
        </Box>
      ) : (
        <Stack
          spacing={1.5}
          sx={{
            overflowY: "auto",
            pr: 0.5,
            flexGrow: 1,
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: colors.primary[400],
              borderRadius: 4,
            },
          }}
        >
          {rows.map((row) => (
            <Box
              key={row.id}
              onClick={() => navigate(`/bookings/${row.id}`)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                border: `1px solid ${colors.primary[800]}`,
                borderRadius: "16px",
                p: 1.25,
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: colors.primary[800],
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Avatar
                src={row.imageUrl || undefined}
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  bgcolor: STATUS_DOT_COLOR[row.status],
                  fontSize: "0.75rem",
                  fontWeight: 700,
                }}
              >
                {row.initials}
              </Avatar>

              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={colors.primary[700]}
                    noWrap
                  >
                    {row.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={colors.primary[400]}
                    noWrap
                    sx={{ flexShrink: 0 }}
                  >
                    · {row.bookingType}
                  </Typography>
                </Box>
                <Typography variant="caption" color={colors.primary[300]} noWrap display="block">
                  {row.venue}
                </Typography>
                <Typography variant="caption" color={colors.primary[300]} noWrap display="block">
                  {row.dateOfService}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexShrink: 0,
                  bgcolor: `${STATUS_DOT_COLOR[row.status]}1A`,
                  borderRadius: "999px",
                  px: 1.25,
                  py: 0.4,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: STATUS_DOT_COLOR[row.status],
                  }}
                />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ color: STATUS_DOT_COLOR[row.status] }}
                >
                  {STATUS_LABELS[row.status] || row.status}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default MyAssignedBookings;
